import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { submitToCustomerIO } from '@/utils/apiUtils';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
const LAResRebuildingCup = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const {
    toast
  } = useToast();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitToCustomerIO({
        email,
        name,
        company,
        message: `LA Resilient Rebuilding Cup Lead: ${message}`
      }, 'demo-request');
      toast({
        title: "Demo request submitted!",
        description: "We'll be in touch shortly."
      });

      // Reset form
      setEmail('');
      setName('');
      setCompany('');
      setMessage('');
    } catch (error) {
      toast({
        title: "Error submitting request",
        description: error instanceof Error ? error.message : "Please try again later.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Fordje Demo - LA Resilient Rebuilding Cup",
    "description": "Request a demo of Fordje's regulatory data platform for resilient rebuilding and climate action",
    "url": "https://www.fordje.com/la-resilient-rebuilding-cup",
    "mainEntity": {
      "@type": "Organization",
      "name": "Fordje",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "Sales",
        "availableLanguage": "English"
      }
    }
  };
  return <div className="min-h-screen">
      <Header />
      <div className="bg-gradient-to-b from-fordje-gray-light to-white">{/* Adding padding-top for fixed header */}
      <Helmet>
        <title>Fordje Demo - LA Resilient Rebuilding Cup | Regulatory Data for Climate Action</title>
        <meta name="robots" content="noindex, follow" />
        <meta name="description" content="Fordje gives solar, storage, and EV teams every local regulation and utility requirement for a jurisdiction, from feasibility through inspection." />
        <meta name="keywords" content="LA resilient rebuilding, climate action building codes, resilient construction, fire-resistant building, LA rebuilding cup, regulatory data, building permits" />
        <link rel="canonical" href="https://www.fordje.com/la-resilient-rebuilding-cup" />
        
        <meta property="og:title" content="Fordje Demo - LA Resilient Rebuilding Cup" />
        <meta property="og:description" content="Fordje gives solar, storage, and EV teams every local regulation and utility requirement for a jurisdiction, from feasibility through inspection." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.fordje.com/la-resilient-rebuilding-cup" />
        <meta property="og:image" content="https://www.fordje.com/og-image.png" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Fordje Demo - LA Resilient Rebuilding Cup" />
        <meta name="twitter:description" content="Fordje gives solar, storage, and EV teams every local regulation and utility requirement for a jurisdiction, from feasibility through inspection." />
        <meta name="twitter:image" content="https://www.fordje.com/og-image.png" />
        
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      
      <main className="overflow-hidden">
        {/* Hero Section */}
        <section className="relative overflow-hidden pt-24 pb-16">{/* Increased pt for header */}
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1574362848149-11496d93a7c7?ixlib=rb-1.2.1&auto=format&fit=crop&w=1920&q=80')] opacity-10"></div>
          
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-5xl mx-auto text-center">
              {/* Fordje Logo/Brand */}
              <div className="mb-8">
                <h1 className="text-4xl md:text-6xl font-extrabold text-fordje-navy mb-4">
                  <span className="bg-gradient-to-r from-fordje-blue to-fordje-navy bg-clip-text text-transparent">
                    Fordje
                  </span>
                </h1>
                <p className="text-fordje-navy-light text-3xl">Build fast with clear city codes</p>
              </div>

              {/* LA Resilient Rebuilding Cup Badge */}
              <div className="bg-fordje-blue/10 border border-fordje-blue/20 rounded-full px-6 py-3 inline-block mb-8">
                <p className="text-fordje-blue font-semibold text-sm">
                  🏆 LA Resilient Rebuilding Cup Finalist
                </p>
              </div>

              <h2 className="text-3xl md:text-5xl font-bold text-fordje-navy mb-6 leading-tight">
                Building LA's Climate-Resilient Future
              </h2>
              
              <p className="text-xl md:text-2xl mb-8 text-fordje-navy-light max-w-4xl mx-auto leading-relaxed">
                Navigate complex building codes and regulations to accelerate 
                <span className="text-fordje-blue font-semibold"> resilient rebuilding</span> and 
                <span className="text-fordje-blue font-semibold"> clean tech projects</span> across LA and beyond.
              </p>

              {/* Navigation Links */}
              <div className="flex gap-4 justify-center mb-8">
                <Button variant="outline" asChild className="border-fordje-blue text-fordje-blue hover:bg-fordje-blue hover:text-white">
                  <a href="/">Learn More About Fordje</a>
                </Button>
                <Button variant="outline" asChild className="border-fordje-blue text-fordje-blue hover:bg-fordje-blue hover:text-white">
                  <a href="/blog">Read Our Blog</a>
                </Button>
              </div>

              {/* Key Stats */}
              
            </div>
          </div>
        </section>
        
        {/* Demo Request Form */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto">
              <div className="bg-fordje-navy p-8 md:p-12 rounded-lg shadow-xl">
                <div className="text-center mb-8">
                  <h3 className="text-3xl font-semibold mb-4 text-white">
                    Demo with Founder
                  </h3>
                  <p className="text-fordje-gray text-lg">
                    Reach out to schedule a personalized demo with one of our founders.
                  </p>
                  
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-white">Full Name</Label>
                    <Input id="name" value={name} onChange={e => setName(e.target.value)} required placeholder="Your name" className="bg-fordje-navy-light border-fordje-navy-light text-white" maxLength={100} />
                  </div>
                  
                  <div>
                    <Label htmlFor="email" className="text-white">Email Address</Label>
                    <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="your.email@company.com" className="bg-fordje-navy-light border-fordje-navy-light text-white" maxLength={100} />
                  </div>
                  
                  <div>
                    <Label htmlFor="company" className="text-white">Company/Organization</Label>
                    <Input id="company" value={company} onChange={e => setCompany(e.target.value)} required placeholder="Your company or organization" className="bg-fordje-navy-light border-fordje-navy-light text-white" maxLength={100} />
                  </div>
                  
                  <div>
                    <Label htmlFor="message" className="text-white">What do you want to discuss? (Optional)</Label>
                    <Textarea id="message" value={message} onChange={e => setMessage(e.target.value)} placeholder="Tell us about your resilient building, fire prevention, or climate action project" className="bg-fordje-navy-light border-fordje-navy-light text-white min-h-[120px]" maxLength={500} />
                  </div>
                  
                  <Button type="submit" className="w-full bg-fordje-blue hover:bg-fordje-blue-dark text-white py-6 h-auto text-lg font-semibold" disabled={loading}>
                    {loading ? 'Submitting...' : 'Schedule Demo with Founder'}
                  </Button>
                  
                  <p className="text-sm text-fordje-gray text-center mt-4">
                    We respect your privacy and will never share your information.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Additional Context Section */}
        <section className="py-12 bg-white/50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h4 className="text-2xl font-semibold text-fordje-navy mb-6">
                Supporting LA's Vision for 2028 and Beyond
              </h4>
              <p className="text-lg text-fordje-navy-light leading-relaxed">As a finalist in the LA Resilient Rebuilding Cup, Fordje is committed to helping Los Angeles build back stronger and more sustainably. Our AI-powered platform accelerates critical housing and clean tech projects that will help LA welcome the world in 2028.</p>
            </div>
          </div>
        </section>
      </main>
      </div>
      <Footer />
    </div>;
};
export default LAResRebuildingCup;